import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { serviceProviderService } from '../services/serviceProviderService';
import { ServiceProvider, Review } from '../types/firestore';
import { useAuth } from '../contexts/AuthContext';
import { reviewService } from '../services/reviewService';
import {
  canCreateRepairRequestForServiceProvider,
  canShowRepairRequestCtaForServiceProvider,
  isOwnServiceProviderTarget,
  isServiceAdmin,
} from '../utils/accessControl';
import { Button, Alert, Badge } from '../components/common/UIComponents';
import {
  PageEmptyState,
  PageErrorState,
  PageLoadingState,
  PageNotFoundState,
} from '../components/common/ViewStates';
import {
  BuildingStorefrontIcon,
  ChatBubbleBottomCenterTextIcon,
  StarIcon,
  PhoneIcon,
  EnvelopeIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import StarRatingDisplay from '../components/common/StarRatingDisplay';
import LoginPromptModal from '../components/common/LoginPromptModal';
import {
  getPublicProfileBackNavigation,
  resolvePublicProfileEntryContext,
} from '../utils/publicProfileNavigation';
import {
  getBusinessHoursDisplayRows,
  resolveServiceAvailabilityStatus,
} from '../utils/businessHours';
import { formatServiceHourlyRateLabel, getPublicServiceHourlyRate } from '../utils/servicePricing';
import {
  formatServiceCapabilityIntents,
  providerCapabilityCategoryById,
  providerCapabilityObjectById,
} from '../utils/serviceCapabilities';
import { ServiceWorkspaceShell } from '../components/Service/ServiceWorkspaceShell';
import ServiceWorkspaceBackLink from '../components/Service/ServiceWorkspaceBackLink';
import ServiceProviderProfileHero from '../components/ServiceProvider/ServiceProviderProfileHero';
import ServiceFulfillmentSummary from '../components/ServiceProvider/ServiceFulfillmentSummary';

const formatDate = (timestamp: any): string => {
  if (!timestamp) return 'Nieznana data';
  if (typeof timestamp.toDate === 'function') {
    return timestamp.toDate().toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
  if (timestamp instanceof Date) {
    return timestamp.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
  return 'Nieznana data';
};

const getReviewsCount = (profile: ServiceProvider): number => {
  return profile.reviewsCount ?? profile.numberOfRatings ?? 0;
};

const getAverageRating = (profile: ServiceProvider): number => {
  return profile.averageRating ?? 0;
};

const formatReviewAuthorName = (value: string | null | undefined): string => {
  const raw = (value || '').trim().replace(/\s+/g, ' ');
  if (!raw) return 'Klient';

  const parts = raw.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    const firstName = parts[0];
    const surnameInitial = parts[1].charAt(0).toUpperCase();
    return `${firstName} ${surnameInitial}.`;
  }

  return parts[0];
};

const formatCountLabel = (
  count: number,
  singular: string,
  paucal: string,
  plural: string
): string => {
  if (count === 1) return singular;
  if (count < 5) return paucal;
  return plural;
};

const normalizePhoneHref = (value: string | null | undefined): string | null => {
  const normalized = (value || '').trim().replace(/[^\d+]/g, '');
  return normalized.length > 0 ? normalized : null;
};

const formatAvailabilitySummaryText = (
  status: ReturnType<typeof resolveServiceAvailabilityStatus>
): string => {
  if (status.state === 'open') {
    const sameDayClosingMatch = status.helperText?.match(/^Dziś do (\d{2}:\d{2})$/u);
    if (sameDayClosingMatch) {
      return `Otwarte do ${sameDayClosingMatch[1]}`;
    }
    return 'Otwarte';
  }

  if (status.state === 'closed') {
    if (!status.helperText) {
      return 'Zamknięte';
    }

    const nextOpeningMatch = status.helperText.match(/^(.+?) od (\d{2}:\d{2})$/u);
    if (nextOpeningMatch) {
      const [, dayLabel, time] = nextOpeningMatch;
      return `Zamknięte · ${dayLabel.toLowerCase()} ${time}`;
    }

    return `Zamknięte · ${status.helperText}`;
  }

  if (status.state === 'temporarily_unavailable') {
    return status.helperText ? `${status.label} · ${status.helperText}` : status.label;
  }

  return 'Godziny niepodane';
};

const getAvailabilitySummaryClassName = (
  status: ReturnType<typeof resolveServiceAvailabilityStatus>
): string => {
  if (status.state === 'open') {
    return 'text-status-success-400';
  }

  if (status.state === 'temporarily_unavailable') {
    return 'text-status-warning-400';
  }

  return 'text-primary-light-100';
};

const ReviewDisplayCard: React.FC<{ review: Review }> = ({ review }) => {
  const contextParts = [
    review.requestSnapshot?.deviceType,
    review.requestSnapshot?.brand,
    review.requestSnapshot?.model,
  ].filter(Boolean);
  const authorName = formatReviewAuthorName(review.clientDisplayName);
  const reviewComment = review.comment?.trim() || 'Klient nie dodał komentarza.';

  return (
    <article className="rs-profile-review px-3.5 py-3.5 sm:px-4 sm:py-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="space-y-2 md:w-64 md:flex-shrink-0">
          <div className="flex items-start justify-between gap-3 md:flex-col md:items-start">
            <div>
              <p
                className="max-w-[14rem] text-sm font-semibold text-primary-light-100 truncate"
                title={authorName}
              >
                {authorName}
              </p>
              <p className="text-xs text-primary-light-400">{formatDate(review.createdAt)}</p>
            </div>
            <Badge variant="success" size="sm">
              Zweryfikowane zlecenie
            </Badge>
          </div>

          <div className="flex items-center gap-2">
            <StarRatingDisplay rating={review.rating} size={16} />
            <span className="text-sm text-primary-light-200 font-medium">
              {review.rating.toFixed(1)}/5
            </span>
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm leading-relaxed text-primary-light-200 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
            {reviewComment}
          </p>

          {contextParts.length > 0 && (
            <p className="text-xs text-primary-light-400 mt-3 break-words [overflow-wrap:anywhere]">
              Naprawa: {contextParts.join(' | ')}
            </p>
          )}
        </div>
      </div>
    </article>
  );
};

const ServiceProviderPublicProfilePage: React.FC = () => {
  const { providerId } = useParams<{ providerId: string }>();
  const { currentUser, userProfile, claims } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState<ServiceProvider | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewsError, setReviewsError] = useState<string | null>(null);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [isBusinessHoursExpanded, setIsBusinessHoursExpanded] = useState(false);
  const [demoFeedback, setDemoFeedback] = useState<string | null>(null);

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [loginModalAction, setLoginModalAction] = useState('wykonać tę akcję');
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const entryContext = resolvePublicProfileEntryContext(
    (location.state as { publicProfileFrom?: unknown; from?: unknown } | null) ?? null,
    searchParams
  );
  const isOwnServiceTarget = isOwnServiceProviderTarget(
    currentUser?.uid || null,
    providerId || null
  );
  const isOwnerPreview = Boolean(
    currentUser && providerId && isOwnServiceTarget && isServiceAdmin(userProfile, claims)
  );
  const canStartRequestForViewedProvider = canCreateRepairRequestForServiceProvider(
    userProfile,
    claims,
    currentUser?.uid || null,
    providerId || null
  );
  const canShowRequestCtaForViewedProvider = canShowRepairRequestCtaForServiceProvider(
    userProfile,
    claims,
    currentUser?.uid || null,
    providerId || null,
    profile?.isTestData === true
  );
  const backNavigation = useMemo(
    () => getPublicProfileBackNavigation(entryContext),
    [entryContext]
  );
  const isServiceWorkspaceMode = isOwnerPreview;

  const renderWorkspaceFrame = (
    content: React.ReactElement,
    maxWidthClass = 'max-w-4xl'
  ): React.ReactElement => {
    if (!isServiceWorkspaceMode) {
      return content;
    }

    return (
      <ServiceWorkspaceShell containerClassName="max-w-5xl" contentClassName="p-0">
        <div className={`mx-auto w-full ${maxWidthClass}`}>{content}</div>
      </ServiceWorkspaceShell>
    );
  };

  useEffect(() => {
    const fetchProfileAndReviews = async () => {
      if (!providerId) {
        setError('Brak ID serwisu w adresie URL.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadingReviews(true);
      setError(null);
      setReviewsError(null);
      setShowAllReviews(false);

      try {
        const serviceProfile =
          await serviceProviderService.getPublicDiscoverableServiceProviderProfile(providerId);
        if (!serviceProfile) {
          setError('Ten serwis jest obecnie niedostępny.');
          setLoading(false);
          setLoadingReviews(false);
          return;
        }

        setProfile(serviceProfile);
        setLoading(false);

        try {
          const requestedReviewCount = Math.max(50, Math.min(getReviewsCount(serviceProfile), 150));
          const fetchedReviews = await reviewService.getReviewsForService(
            providerId,
            requestedReviewCount
          );
          setReviews(
            [...fetchedReviews].sort((a, b) => {
              const left =
                typeof b.createdAt?.toDate === 'function' ? b.createdAt.toDate().getTime() : 0;
              const right =
                typeof a.createdAt?.toDate === 'function' ? a.createdAt.toDate().getTime() : 0;
              return left - right;
            })
          );
        } catch (reviewFetchError) {
          console.error('Błąd pobierania opinii:', reviewFetchError);
          setReviewsError('Nie udało się pobrać opinii klientów.');
        } finally {
          setLoadingReviews(false);
        }
      } catch (profileError) {
        console.error('Błąd pobierania profilu serwisu:', profileError);
        setError('Nie udało się pobrać profilu serwisu.');
        setLoading(false);
        setLoadingReviews(false);
      }
    };

    fetchProfileAndReviews();
  }, [providerId]);

  const handleProtectedAction = (actionType: string, actionFn: () => void) => {
    if (!currentUser) {
      setLoginModalAction(actionType);
      setIsLoginModalOpen(true);
      return;
    }
    actionFn();
  };

  const reviewsCount = useMemo(() => {
    if (!profile) return 0;
    return getReviewsCount(profile);
  }, [profile]);

  const averageRating = useMemo(() => {
    if (!profile) return 0;
    return getAverageRating(profile);
  }, [profile]);

  const displayedReviews = useMemo(
    () => (showAllReviews ? reviews : reviews.slice(0, 3)),
    [reviews, showAllReviews]
  );

  const hiddenReviewsCount = Math.max(reviews.length - displayedReviews.length, 0);
  const sectionReviewsCount =
    !loadingReviews && !reviewsError ? Math.max(reviews.length, reviewsCount) : reviewsCount;
  const requestReturnTo = providerId ? { pathname: `/app/request/new/${providerId}` } : null;
  const businessHoursStatus = useMemo(() => resolveServiceAvailabilityStatus(profile), [profile]);
  const businessHoursRows = useMemo(
    () => getBusinessHoursDisplayRows(profile?.businessHours),
    [profile?.businessHours]
  );
  const publicHourlyRate = useMemo(() => getPublicServiceHourlyRate(profile), [profile]);

  useEffect(() => {
    setIsBusinessHoursExpanded(false);
  }, [profile?.uid]);

  useEffect(() => {
    setDemoFeedback(null);
  }, [profile?.uid]);

  if (loading) {
    return renderWorkspaceFrame(
      <div className="py-4 md:py-6">
        <PageLoadingState
          title="Ładowanie profilu serwisu"
          message="Pobieramy dane firmy i opinie."
        />
      </div>,
      'max-w-2xl'
    );
  }

  if (error) {
    return renderWorkspaceFrame(
      <div className="py-4 md:py-6">
        <PageErrorState
          title="Nie udało się załadować profilu serwisu"
          message={error}
          actionLabel={backNavigation.label}
          onAction={() => navigate(backNavigation.target)}
        />
      </div>,
      'max-w-xl'
    );
  }

  if (!profile) {
    return renderWorkspaceFrame(
      <div className="py-4 md:py-6">
        <PageNotFoundState
          title="Nie znaleziono serwisu"
          message="Ten profil może być niedostępny albo został usunięty."
          actionLabel={backNavigation.label}
          onAction={() => navigate(backNavigation.target)}
        />
      </div>,
      'max-w-xl'
    );
  }

  const descriptionText = (profile.description || '').trim();
  const rawCapabilityItems = profile.serviceCapabilities?.items;
  const capabilityItems = Array.isArray(rawCapabilityItems) ? rawCapabilityItems : [];
  const capabilityCards = capabilityItems
    .map((item) => {
      const category = providerCapabilityCategoryById.get(item.categoryId);
      if (!category) {
        return null;
      }

      const selectedObjects = item.objectSelections
        .map((selection) => providerCapabilityObjectById.get(selection.objectId))
        .filter((object): object is NonNullable<typeof object> => Boolean(object));
      const exclusionLabels = item.excludedObjectIds
        .map((objectId) => providerCapabilityObjectById.get(objectId))
        .filter((object): object is NonNullable<typeof object> => Boolean(object))
        .map((object) => object.pluralLabel || object.label);
      const visibleObjectLabels = selectedObjects
        .slice(0, 3)
        .map((object) => object.pluralLabel || object.label);
      const remainingObjectsCount = Math.max(
        selectedObjects.length - visibleObjectLabels.length,
        0
      );

      return {
        id: item.id,
        categoryLabel: category.label,
        intentsLabel: formatServiceCapabilityIntents(item.intents),
        detailLabel:
          item.mode === 'categoryWide'
            ? 'Cały obszar'
            : visibleObjectLabels.length > 0
              ? visibleObjectLabels.join(', ')
              : 'Wybrane urządzenia',
        remainingObjectsCount,
        exclusionLabel:
          item.mode === 'categoryWide' && exclusionLabels.length > 0
            ? `Bez: ${exclusionLabels.join(', ')}`
            : null,
      };
    })
    .filter((item): item is NonNullable<typeof item> => Boolean(item));
  const specializations =
    capabilityCards.length === 0
      ? (profile.specializations || []).filter((value): value is string => value.trim().length > 0)
      : [];
  const isTestProfile = profile.isTestData === true;
  const phoneNumber = profile.phoneNumber?.trim() || '';
  const emailAddress = profile.email?.trim() || '';
  const phoneHref = isTestProfile ? null : normalizePhoneHref(phoneNumber);
  const emailHref = isTestProfile || !emailAddress ? null : `mailto:${emailAddress}`;
  const availabilitySummaryText = formatAvailabilitySummaryText(businessHoursStatus);
  const availabilitySummaryClassName = getAvailabilitySummaryClassName(businessHoursStatus);
  const availabilityDetailText =
    businessHoursStatus.helperText?.trim() || formatAvailabilitySummaryText(businessHoursStatus);
  const shouldShowBackAction = isOwnerPreview || entryContext !== null;
  const shouldShowExpertiseSection =
    isOwnerPreview ||
    descriptionText.length > 0 ||
    capabilityCards.length > 0 ||
    specializations.length > 0;
  const shouldShowAvailabilitySection = true;
  const canShowRequestEntryForViewedProvider =
    Boolean(providerId) &&
    (isTestProfile
      ? canShowRepairRequestCtaForServiceProvider(
          userProfile,
          claims,
          currentUser?.uid || null,
          providerId || null,
          false
        )
      : canShowRequestCtaForViewedProvider);
  const shouldShowPrimaryRequestPreview = isOwnerPreview && Boolean(providerId);
  const shouldShowRequestEligibilityAlert =
    !isOwnerPreview && Boolean(currentUser) && !canStartRequestForViewedProvider;
  const actionSurfaceClassName =
    'rs-profile-secondary-action inline-flex min-h-[42px] w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-primary-light-100 transition-colors focus-outline lg:min-h-[38px] lg:py-2';
  const actionLinkClassName = actionSurfaceClassName;
  const actionButtonClassName = `${actionSurfaceClassName} disabled:cursor-not-allowed disabled:text-primary-light-500`;
  const compactContactMeta = [
    businessHoursStatus.state !== 'unknown' ? availabilitySummaryText : null,
    publicHourlyRate ? formatServiceHourlyRateLabel(publicHourlyRate.amount) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const compactReviewsSummary =
    sectionReviewsCount > 0
      ? `${averageRating.toFixed(1)}/5 · ${sectionReviewsCount} ${formatCountLabel(
          sectionReviewsCount,
          'zweryfikowana opinia',
          'zweryfikowane opinie',
          'zweryfikowanych opinii'
        )}`
      : 'Brak zweryfikowanych opinii';
  const hasReviewAggregateDrift =
    !loadingReviews && !reviewsError && reviews.length === 0 && reviewsCount > 0;
  const showTestProfileFeedback = () =>
    setDemoFeedback('To profil testowy — akcja jest wyłączona w demo.');

  const pageContent = (
    <div
      className={`mx-auto w-full max-w-6xl ${
        isServiceWorkspaceMode ? 'space-y-4 sm:space-y-5' : 'space-y-5 sm:space-y-6'
      }`}
    >
      {isServiceWorkspaceMode ? (
        <div className="space-y-1">
          <ServiceWorkspaceBackLink onClick={() => navigate('/app/service')} />
          <p className="px-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-primary-light-400">
            Podgląd profilu publicznego
          </p>
        </div>
      ) : shouldShowBackAction ? (
        <div className="flex items-center">
          <Button
            variant="neutral"
            size="sm"
            onClick={() => navigate(backNavigation.target)}
            className="!px-3"
          >
            {backNavigation.label}
          </Button>
        </div>
      ) : null}

      <section className="grid gap-4 lg:grid-cols-[minmax(0,1.42fr)_minmax(19rem,0.78fr)] lg:items-start">
        <div className="rs-profile-identity-layer p-4 sm:p-5">
          <ServiceProviderProfileHero
            profile={profile}
            reviewsCount={reviewsCount}
            averageRating={averageRating}
            variant="hero"
          />
        </div>

        <aside className="rs-profile-contact-layer p-4">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <h2 className="text-base font-semibold text-primary-light-100">
                Kontakt i zgłoszenie
              </h2>
              {compactContactMeta && (
                <p
                  className={`max-w-full text-sm font-medium leading-snug [overflow-wrap:anywhere] ${availabilitySummaryClassName}`}
                  data-testid="public-contact-compact-meta"
                >
                  {compactContactMeta}
                </p>
              )}
            </div>

            <div className="space-y-2">
              {shouldShowPrimaryRequestPreview ? (
                <div>
                  <Button
                    variant="primary"
                    size="lg"
                    fullWidth
                    disabled
                    className="!shadow-none"
                    icon={<PencilSquareIcon className="h-5 w-5" />}
                  >
                    Zgłoś naprawę
                  </Button>
                </div>
              ) : canShowRequestEntryForViewedProvider && providerId ? (
                <div>
                  <Button
                    variant="primary"
                    size="lg"
                    fullWidth
                    className="!shadow-none"
                    onClick={() =>
                      isTestProfile
                        ? showTestProfileFeedback()
                        : handleProtectedAction('zgłosić naprawę', () =>
                            navigate(`/app/request/new/${providerId}`)
                          )
                    }
                    icon={<PencilSquareIcon className="h-5 w-5" />}
                  >
                    Zgłoś naprawę
                  </Button>
                </div>
              ) : shouldShowRequestEligibilityAlert ? (
                <Alert variant="warning" className="!p-3">
                  Zgłoszenie naprawy wymaga aktywnego konta klienta, administratora albo aktywnego
                  serwisu.
                </Alert>
              ) : null}

              {phoneHref ? (
                <a href={`tel:${phoneHref}`} className={actionLinkClassName}>
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" />
                  Zadzwoń
                </a>
              ) : isTestProfile && phoneNumber ? (
                <button
                  type="button"
                  onClick={showTestProfileFeedback}
                  className={actionButtonClassName}
                >
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" />
                  Zadzwoń
                </button>
              ) : null}

              {emailHref ? (
                <a href={emailHref} className={actionLinkClassName}>
                  <EnvelopeIcon className="h-4 w-4" aria-hidden="true" />
                  Wyślij e-mail
                </a>
              ) : isTestProfile && emailAddress ? (
                <button
                  type="button"
                  onClick={showTestProfileFeedback}
                  className={actionButtonClassName}
                >
                  <EnvelopeIcon className="h-4 w-4" aria-hidden="true" />
                  Wyślij e-mail
                </button>
              ) : null}

              {demoFeedback && (
                <p role="status" aria-live="polite" className="text-xs text-primary-light-400">
                  {demoFeedback}
                </p>
              )}
            </div>
          </div>
        </aside>
      </section>

      {shouldShowExpertiseSection && (
        <section className="space-y-3 px-1 sm:px-0">
          <h2 className="flex items-center gap-2 text-base font-semibold text-primary-light-100 sm:text-lg">
            <ChatBubbleBottomCenterTextIcon className="h-5 w-5 text-ratuj-300" />
            Czym się zajmują
          </h2>

          <div
            className={`grid gap-3 ${
              descriptionText.length > 0 && specializations.length > 0
                ? 'lg:grid-cols-[minmax(0,1.24fr)_minmax(0,0.96fr)] lg:gap-5'
                : ''
            }`}
          >
            {descriptionText && (
              <p className="max-w-3xl whitespace-pre-wrap text-sm leading-relaxed text-primary-light-300 sm:text-base">
                {descriptionText}
              </p>
            )}

            {capabilityCards.length > 0 && (
              <div className="grid gap-2.5">
                {capabilityCards.map((capability) => (
                  <div key={capability.id} className="rs-profile-content-layer px-3.5 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-primary-light-100">
                        {capability.categoryLabel}
                      </p>
                      <Badge variant="neutral" size="sm">
                        {capability.intentsLabel}
                      </Badge>
                    </div>
                    <p className="mt-2 text-sm text-primary-light-300">{capability.detailLabel}</p>
                    {capability.remainingObjectsCount > 0 ? (
                      <p className="mt-1 text-xs font-semibold text-primary-light-400">
                        +{capability.remainingObjectsCount}
                      </p>
                    ) : null}
                    {capability.exclusionLabel ? (
                      <p className="mt-1 text-xs text-primary-light-400">
                        {capability.exclusionLabel}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
            )}

            {specializations.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {specializations.map((specialization, index) => (
                  <Badge key={`${specialization}-${index}`} variant="neutral">
                    {specialization}
                  </Badge>
                ))}
              </div>
            )}

            {isOwnerPreview && descriptionText.length === 0 && specializations.length === 0 && (
              <div className="rs-profile-content-layer px-3.5 py-3">
                <p className="text-sm text-primary-light-300">
                  Uzupełnij zakres usług w danych serwisu.
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      <section className="grid gap-5 px-1 sm:px-0 lg:grid-cols-[minmax(0,1.06fr)_minmax(18rem,0.94fr)] lg:gap-6">
        <div className="min-w-0">
          <ServiceFulfillmentSummary provider={profile} />
        </div>

        {shouldShowAvailabilitySection && (
          <div className="min-w-0 space-y-3">
            <h2 className="flex items-center gap-2 text-base font-semibold text-primary-light-100 sm:text-lg">
              <BuildingStorefrontIcon className="h-5 w-5 text-ratuj-300" />
              Dostępność i cena
            </h2>

            {publicHourlyRate && (
              <div className="rs-profile-content-layer px-3.5 py-3">
                <p className="text-xs uppercase tracking-wide text-primary-light-400">
                  Stawka orientacyjna
                </p>
                <p className="mt-1 text-base font-semibold text-primary-light-100">
                  {formatServiceHourlyRateLabel(publicHourlyRate.amount)}
                </p>
                <p className="mt-1 text-sm text-primary-light-300">
                  Ostateczna wycena powstaje po diagnozie.
                </p>
              </div>
            )}

            {businessHoursStatus.state === 'unknown' ? (
              <div className="rs-profile-content-layer px-3.5 py-3">
                <p className="text-sm font-medium text-primary-light-400">Godziny niepodane</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="rs-profile-content-layer px-3.5 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold ${availabilitySummaryClassName}`}>
                        {availabilityDetailText}
                      </p>
                      {businessHoursStatus.state === 'temporarily_unavailable' &&
                        businessHoursStatus.description && (
                          <p className="mt-1 text-xs leading-relaxed text-primary-light-300">
                            {businessHoursStatus.description}
                          </p>
                        )}
                    </div>
                    <Button
                      variant="neutral"
                      size="sm"
                      onClick={() => setIsBusinessHoursExpanded((current) => !current)}
                      aria-expanded={isBusinessHoursExpanded}
                      className="rs-profile-inline-action shrink-0"
                    >
                      {isBusinessHoursExpanded ? 'Zwiń tydzień' : 'Pokaż tydzień'}
                    </Button>
                  </div>
                </div>

                {isBusinessHoursExpanded && (
                  <div className="rs-soft-row-group overflow-hidden">
                    <div>
                      {businessHoursRows.map((row) => (
                        <div
                          key={row.key}
                          className={`rs-soft-row flex items-center justify-between gap-3 px-3.5 py-2.5 text-xs sm:text-sm ${
                            row.isToday ? 'bg-primary-dark-500/38' : 'bg-transparent'
                          }`}
                        >
                          <span
                            className={
                              row.isToday
                                ? 'font-semibold text-primary-light-100'
                                : 'text-primary-light-300'
                            }
                          >
                            {row.label}
                          </span>
                          <span
                            className={
                              row.isToday
                                ? 'font-semibold text-primary-light-100'
                                : 'text-primary-light-300'
                            }
                          >
                            {row.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      <section className="space-y-3 px-1 sm:px-0">
        <div className="space-y-3">
          <h2 className="flex items-center gap-2 text-base font-semibold text-primary-light-100 sm:text-lg">
            <StarIcon className="h-5 w-5 text-gold-300" />
            Opinie klientów
          </h2>
          <div
            className="rs-profile-review-summary flex flex-wrap items-center gap-2 px-3.5 py-3"
            data-testid="public-reviews-compact-summary"
          >
            <StarRatingDisplay rating={averageRating} size={18} />
            <p className="text-sm font-semibold text-primary-light-100">{compactReviewsSummary}</p>
          </div>
        </div>

        {loadingReviews && (
          <PageLoadingState
            title="Ładowanie opinii"
            message="Pobieramy ostatnie opinie klientów."
            className="py-6"
          />
        )}

        {reviewsError && !loadingReviews && <Alert variant="error">{reviewsError}</Alert>}

        {hasReviewAggregateDrift && (
          <Alert variant="warning">
            Ten profil ma zapisany agregat ocen, ale lista opinii nie jest jeszcze dostępna. To
            sygnał rozjazdu danych źródłowych, nie brak opinii.
          </Alert>
        )}

        {!loadingReviews && !reviewsError && reviews.length === 0 && !hasReviewAggregateDrift && (
          <PageEmptyState
            title="Brak opinii"
            message="Ten serwis nie ma jeszcze opinii ze zweryfikowanych zleceń."
            className="py-6"
          />
        )}

        {!loadingReviews && !reviewsError && reviews.length > 0 && (
          <div className="space-y-3.5">
            {displayedReviews.map((review) => (
              <ReviewDisplayCard key={review.id ?? review.uid} review={review} />
            ))}
            {hiddenReviewsCount > 0 && (
              <div className="pt-1">
                <Button
                  variant="neutral"
                  size="sm"
                  onClick={() => setShowAllReviews(true)}
                  className="w-full sm:w-auto"
                >
                  Pokaż więcej ({hiddenReviewsCount})
                </Button>
              </div>
            )}
            {showAllReviews && reviews.length > 3 && (
              <div className="pt-1">
                <Button
                  variant="neutral"
                  size="sm"
                  onClick={() => setShowAllReviews(false)}
                  className="w-full sm:w-auto"
                >
                  Pokaż mniej
                </Button>
              </div>
            )}
          </div>
        )}
      </section>

      <LoginPromptModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        actionType={loginModalAction}
        returnTo={requestReturnTo}
      />
    </div>
  );

  if (isServiceWorkspaceMode) {
    return renderWorkspaceFrame(pageContent, 'max-w-6xl');
  }

  return <div className="rs-page-gutter py-3 md:py-6">{pageContent}</div>;
};

export default ServiceProviderPublicProfilePage;
